// Port of TrailNavigation/MultiDayPlanner.swift (OvernightAnchor, OvernightAnchorBuilder, MultiDayPlanRequest,
// PlanDayWarning, PlannedDay, MultiDayPlanner).

import type {
  EpochMs,
  GeoCoordinate,
  OvernightCategory,
  OvernightPreference,
  PointOfInterest,
} from '../core/models';
import { boundsContains, boundsExpanded, boundsOf } from '../core/geo';
import { overnightCategoryOf, preferredCategories } from '../core/domain';
import { localCalendar, type Calendar } from './calendar';
import type { HikingTimeEstimator } from './hikingTimeEstimator';
import { NavigationLog } from './navigationLog';
import type { RouteTrack } from './routeTrack';

/** A place along the route where a night can be spent (from POIs or bivouac zones). */
export interface OvernightAnchor {
  /** Distance along the route of the place's projection (m). */
  distanceAlong: number;
  /** Overnight category. */
  category: OvernightCategory;
  /** Data name (POI name); may be empty for bivouac search areas. */
  name: string;
  /** Source POI, if any. */
  poiID?: string;
  /** Location of the place itself (not its projection). */
  coordinate: GeoCoordinate;
  /** Straight-line distance from the route (m) — a lower bound of the real walking detour. */
  detourMeters: number;
}

/** Derives `OvernightAnchor`s from POIs. */
export const OvernightAnchorBuilder = {
  /**
   * POIs whose category can host a night within `maxDetourMeters` of the route, projected onto it. Sorted by
   * distanceAlong, then detour, then id. Bivouac search areas come from the bivouac engine and can be appended by the
   * caller.
   *
   * Only designated places are planned as overnights: a POI of category `shelter` counts only when its accommodation
   * kind is `bivouacHut` (the same rule as the bivouac engine's "official" facilities). Other shelters (emergency or
   * basic huts) are for emergencies, not for planning a night, and are skipped.
   */
  anchors(track: RouteTrack, pointsOfInterest: readonly PointOfInterest[], maxDetourMeters = 1000): OvernightAnchor[] {
    if (!Number.isFinite(maxDetourMeters) || maxDetourMeters < 0) return [];
    const bounds = boundsOf(track.coordinates);
    if (!bounds) return [];
    const area = boundsExpanded(bounds, maxDetourMeters);
    const result: OvernightAnchor[] = [];
    for (const poi of pointsOfInterest) {
      const category = overnightCategoryOf(poi.category);
      if (!category) continue;
      if (poi.category === 'shelter' && poi.attributes.accommodation?.kind !== 'bivouacHut') continue;
      if (!boundsContains(area, poi.coordinate)) continue;
      const projection = track.project(poi.coordinate);
      if (!projection || !(projection.crossTrackDistance <= maxDetourMeters)) continue;
      result.push({
        distanceAlong: projection.distanceAlong,
        category,
        name: poi.name,
        poiID: poi.id,
        coordinate: poi.coordinate,
        detourMeters: projection.crossTrackDistance,
      });
    }
    result.sort((a, b) => {
      if (a.distanceAlong !== b.distanceAlong) return a.distanceAlong - b.distanceAlong;
      if (a.detourMeters !== b.detourMeters) return a.detourMeters - b.detourMeters;
      const ia = a.poiID ?? '';
      const ib = b.poiID ?? '';
      return ia < ib ? -1 : ia > ib ? 1 : 0;
    });
    return result;
  },
};

/** Input of `MultiDayPlanner.plan`. */
export interface MultiDayPlanRequest {
  /** Where the trip starts (m along the route). */
  startDistance: number;
  /** Where the trip ends (m along the route); must be greater than `startDistance`. */
  endDistance: number;
  /** Date of the first day (its time is respected for day 1 when later than `dailyStartHour`). */
  startDate: EpochMs;
  /** Walking-time budget per day (h, breaks excluded). Values below 1 h are raised to 1 h. Default 7. */
  maxWalkingHoursPerDay?: number;
  /** Optional daily distance limit (m). */
  preferredDailyDistance?: number;
  /** Optional daily ascent limit (m). */
  maxAscentPerDay?: number;
  /** Preferred way to spend the night. Default `mixed`. */
  preference?: OvernightPreference;
  /** Local hour at which each day starts (0…23). Default 8. */
  dailyStartHour?: number;
  /** Calendar (and time zone) used for day dates. Default: the device's local time. */
  calendar?: Calendar;
}

/** Caveats attached to a planned day (the app layer renders localized text). */
export type PlanDayWarning =
  /** Estimated walking time exceeds the daily budget (e.g. no stopping point before the limit). */
  | 'exceedsMaxHours'
  /** Ascent exceeds the daily ascent limit. */
  | 'exceedsMaxAscent'
  /** No overnight place of a preferred category near the end of the day. */
  | 'noOvernightMatchingPreference'
  /** The overnight is only a bivouac *search area*: local rules and terrain must be checked on site. */
  | 'overnightIsSearchAreaOnly'
  /** More than 8 h of walking. */
  | 'longDay';

/** One day of a multi-day plan. */
export interface PlannedDay {
  /** Day index, starting at 0. */
  id: number;
  /** Start time of the day. */
  date: EpochMs;
  /** Route distance where the day starts (m). */
  startDistance: number;
  /** Route distance where the day ends (m). */
  endDistance: number;
  /** Distance walked along the route (m, detours excluded). */
  distance: number;
  /** Ascent along the route (m). */
  ascent: number;
  /** Descent along the route (m). */
  descent: number;
  /** Walking time (seconds) including the detours to/from the overnight places, personal factor applied. */
  estimatedDuration: number;
  /** Where the night is spent, if a place was found. */
  overnight?: OvernightAnchor;
  /** The overnight place's location, else the route position at `endDistance`. */
  endCoordinate: GeoCoordinate;
  /** Caveats for the day. */
  warnings: PlanDayWarning[];
}

interface DailyLimits {
  /** seconds */
  duration: number;
  ascent?: number;
  distance?: number;
}

const positiveFinite = (value: number | undefined) =>
  value !== undefined && Number.isFinite(value) && value > 0 ? value : undefined;

/** Splits a section of a route into days that fit the hiker's limits. */
export class MultiDayPlanner {
  /** Step (m) of the forward walk that finds where the daily limits bind. */
  static readonly stepDistance = 250;
  /** Fraction of the day (by distance) at the end of which an overnight place is searched. */
  static readonly anchorWindowFraction = 0.25;
  /** Walking time (s) above which a day is flagged `longDay`. */
  static readonly longDayDuration = 8 * 3600;
  /** Radius (m) around the final destination searched for the last night's place. */
  static readonly destinationRadius = 500;
  /** Safety cap on the number of planned days. */
  static readonly maximumDays = 366;
  /** Precision (m) of the daily limit point inside the last 250 m step. */
  static readonly limitResolution = 1;

  /** The route geometry. */
  readonly track: RouteTrack;
  /** Time estimator (personal factor applied). */
  readonly estimator: HikingTimeEstimator;

  constructor(track: RouteTrack, estimator: HikingTimeEstimator) {
    this.track = track;
    this.estimator = estimator;
  }

  /**
   * Does NOT force official stage boundaries: walks forward until the daily limits bind, then picks the best anchor
   * within the last ~25 % of the day matching the preference (falls back to any category, then to the limit point with
   * a warning).
   *
   * - The forward walk advances in 250 m steps from the day start, accumulating estimated time (including the walk back
   *   from the previous night's detour) and ascent; it stops before the step that would exceed the time budget, the
   *   ascent limit or the preferred daily distance (the *step limit*), then bisects inside that step (to 1 m) for the
   *   *limit point* where the limits actually bind. A day always advances at least one step, flagged with the exceeded
   *   limits.
   * - Anchor choice within `[start + 75 % of the day to the step limit, limit point]`: the preference's category order
   *   (for `mixed`, all established facilities rank equally ahead of bivouac search areas), then the farthest along,
   *   then the smallest detour.
   * - The plan never goes beyond `endDistance`; the last day ends exactly there, and its overnight is the best place
   *   within 500 m of the destination, if any.
   * - Returns an empty plan when the route is empty or `endDistance <= startDistance` (plans are made in route
   *   direction; build a reversed `RouteTrack` to plan the other way).
   */
  plan(request: MultiDayPlanRequest, anchors: readonly OvernightAnchor[]): PlannedDay[] {
    const track = this.track;
    const total = track.totalDistance;
    if (!(total > 0) || !Number.isFinite(request.startDistance) || !Number.isFinite(request.endDistance)) return [];
    const start = Math.min(Math.max(request.startDistance, 0), total);
    const end = Math.min(Math.max(request.endDistance, 0), total);
    if (!(end > start)) return [];
    const preference = request.preference ?? 'mixed';

    const requestedHours = request.maxWalkingHoursPerDay ?? 7;
    const hours = Number.isFinite(requestedHours) ? Math.max(1, requestedHours) : 7;
    const limits: DailyLimits = {
      duration: hours * 3600,
      ascent: positiveFinite(request.maxAscentPerDay),
      distance: positiveFinite(request.preferredDailyDistance),
    };
    const usableAnchors = anchors
      .filter((a) => Number.isFinite(a.distanceAlong) && Number.isFinite(a.detourMeters))
      .sort((a, b) => a.distanceAlong - b.distanceAlong);

    const days: PlannedDay[] = [];
    let cursor = start;
    let previousDetour = 0;
    while (cursor < end) {
      if (days.length >= MultiDayPlanner.maximumDays) {
        NavigationLog.logger.notice(`MultiDayPlanner stopped at the ${MultiDayPlanner.maximumDays}-day cap`);
        break;
      }
      const { step: stepLimit, limit } = this.limitPoints(cursor, end, limits, previousDetour);
      const warnings: PlanDayWarning[] = [];
      let overnight: OvernightAnchor | undefined;
      let dayEnd: number;
      if (limit >= end) {
        dayEnd = end;
        const rankOf = (a: OvernightAnchor) => MultiDayPlanner.rank(a.category, preference) ?? Number.MAX_SAFE_INTEGER;
        for (const candidate of usableAnchors) {
          if (!(Math.abs(candidate.distanceAlong - end) <= MultiDayPlanner.destinationRadius)) continue;
          if (!overnight) {
            overnight = candidate;
            continue;
          }
          const ra = rankOf(candidate);
          const rb = rankOf(overnight);
          const da = Math.abs(candidate.distanceAlong - end);
          const db = Math.abs(overnight.distanceAlong - end);
          const better = ra !== rb ? ra < rb : da !== db ? da < db : candidate.detourMeters < overnight.detourMeters;
          if (better) overnight = candidate;
        }
      } else {
        const windowStart = cursor + (1 - MultiDayPlanner.anchorWindowFraction) * (stepLimit - cursor);
        const candidates = usableAnchors.filter(
          (a) => a.distanceAlong > cursor && a.distanceAlong >= windowStart && a.distanceAlong <= limit,
        );
        const preferred = MultiDayPlanner.bestAnchor(candidates, preference, true);
        if (preferred) {
          overnight = preferred;
        } else {
          warnings.push('noOvernightMatchingPreference');
          overnight = MultiDayPlanner.bestAnchor(candidates, preference, false);
        }
        dayEnd = overnight?.distanceAlong ?? limit;
      }
      if (overnight?.category === 'bivouacSearchArea') warnings.push('overnightIsSearchAreaOnly');

      const { ascent, descent } = track.ascentDescent(cursor, dayEnd);
      const detours = previousDetour + (overnight?.detourMeters ?? 0);
      const duration = this.estimator.duration(dayEnd - cursor + detours, ascent, descent);
      if (duration > limits.duration) warnings.push('exceedsMaxHours');
      if (limits.ascent !== undefined && ascent > limits.ascent) warnings.push('exceedsMaxAscent');
      if (duration > MultiDayPlanner.longDayDuration) warnings.push('longDay');

      days.push({
        id: days.length,
        date: MultiDayPlanner.dayStart(days.length, request),
        startDistance: cursor,
        endDistance: dayEnd,
        distance: dayEnd - cursor,
        ascent,
        descent,
        estimatedDuration: duration,
        overnight,
        endCoordinate: overnight?.coordinate ?? track.coordinateAtDistance(dayEnd) ?? { latitude: 0, longitude: 0 },
        warnings,
      });
      previousDetour = overnight?.detourMeters ?? 0;
      cursor = dayEnd;
    }
    return days;
  }

  // MARK: Private

  /**
   * Farthest point reachable from `cursor` within the limits: `step` is the last whole step within them, `limit` the
   * exact point found by bisection inside the step that exceeded them (to `limitResolution`). Both are one step ahead
   * when even the first step exceeds the limits.
   */
  private limitPoints(
    cursor: number,
    end: number,
    limits: DailyLimits,
    previousDetour: number,
  ): { step: number; limit: number } {
    const isWithinLimits = (point: number): boolean => {
      const { ascent, descent } = this.track.ascentDescent(cursor, point);
      const duration = this.estimator.duration(point - cursor + previousDetour, ascent, descent);
      return (
        duration <= limits.duration &&
        (limits.ascent === undefined || ascent <= limits.ascent) &&
        (limits.distance === undefined || point - cursor <= limits.distance)
      );
    };
    let reached = cursor;
    let exceeded: number | undefined;
    while (reached < end) {
      const next = Math.min(reached + MultiDayPlanner.stepDistance, end);
      if (!isWithinLimits(next)) {
        exceeded = next;
        break;
      }
      reached = next;
    }
    if (!(reached > cursor)) {
      const forced = Math.min(cursor + MultiDayPlanner.stepDistance, end);
      return { step: forced, limit: forced };
    }
    // Time, ascent and distance only grow along the route, so the limit inside the exceeded step can be bisected.
    // Without this, a hut a few metres past a step boundary but within the budget would be missed.
    if (exceeded === undefined) return { step: reached, limit: reached };
    let high = exceeded;
    let low = reached;
    while (high - low > MultiDayPlanner.limitResolution) {
      const middle = (low + high) / 2;
      if (isWithinLimits(middle)) low = middle;
      else high = middle;
    }
    return { step: reached, limit: low };
  }

  /** Preference rank of a category (lower is better); `undefined` when the category does not match. */
  static rank(category: OvernightCategory, preference: OvernightPreference): number | undefined {
    if (preference === 'mixed') return category === 'bivouacSearchArea' ? 1 : 0;
    const index = preferredCategories(preference).indexOf(category);
    return index >= 0 ? index : undefined;
  }

  static bestAnchor(
    candidates: readonly OvernightAnchor[],
    preference: OvernightPreference,
    matchingOnly: boolean,
  ): OvernightAnchor | undefined {
    let best: { rank: number; anchor: OvernightAnchor } | undefined;
    for (const anchor of candidates) {
      const matched = MultiDayPlanner.rank(anchor.category, preference);
      if (matched === undefined && matchingOnly) continue;
      const entry = { rank: matched ?? Number.MAX_SAFE_INTEGER, anchor };
      if (!best || MultiDayPlanner.isBetterAnchor(entry, best)) best = entry;
    }
    return best?.anchor;
  }

  private static isBetterAnchor(
    a: { rank: number; anchor: OvernightAnchor },
    b: { rank: number; anchor: OvernightAnchor },
  ): boolean {
    if (a.rank !== b.rank) return a.rank < b.rank;
    if (a.anchor.distanceAlong !== b.anchor.distanceAlong) return a.anchor.distanceAlong > b.anchor.distanceAlong;
    if (a.anchor.detourMeters !== b.anchor.detourMeters) return a.anchor.detourMeters < b.anchor.detourMeters;
    return (a.anchor.poiID ?? a.anchor.name) < (b.anchor.poiID ?? b.anchor.name);
  }

  static dayStart(index: number, request: MultiDayPlanRequest): EpochMs {
    const calendar = request.calendar ?? localCalendar;
    const hour = Math.min(23, Math.max(0, request.dailyStartHour ?? 8));
    const base = calendar.startOfDay(request.startDate);
    const day = calendar.addingDays(index, base);
    const start = calendar.settingTime(day, hour);
    return index === 0 ? Math.max(start, request.startDate) : start;
  }
}
